import React, { useState, useEffect } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Key } from 'lucide-react';

const JWTDecoderPage = () => {
  const [jwt, setJwt] = useState('');
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState('');

  const decodeJWT = (token) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      setDecoded({ header, payload, signature: parts[2] });
      setError('');
    } catch (err) {
      setError('Invalid JWT token: ' + err.message);
      setDecoded(null);
    }
  };

  useEffect(() => {
    if (jwt.trim()) {
      const timer = setTimeout(() => {
        decodeJWT(jwt.trim());
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setDecoded(null);
      setError('');
    }
  }, [jwt]);

  return (
    <ToolLayout
      title="JWT Decoder"
      description="Decode and inspect JSON Web Tokens (JWT). View header, payload, and signature information."
      icon={Key}
    >
      <div className="space-y-6">
        <div>
          <Label htmlFor="jwt">JWT Token</Label>
          <Textarea
            id="jwt"
            placeholder="Paste your JWT token here..."
            value={jwt}
            onChange={(e) => setJwt(e.target.value)}
            rows={6}
            className="font-mono text-sm"
          />
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {decoded && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Header</CardTitle>
                  <Badge variant="secondary">Algorithm: {decoded.header.alg}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
                  {JSON.stringify(decoded.header, null, 2)}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payload</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
                  {JSON.stringify(decoded.payload, null, 2)}
                </pre>
                {decoded.payload.exp && (
                  <div className="mt-4 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                      Expires: {new Date(decoded.payload.exp * 1000).toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Signature</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono text-sm break-all bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                  {decoded.signature}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

export default JWTDecoderPage;